import Seo from "@/components/Seo";
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSite, siteText } from "@/context/SiteContext";
import { toast } from "sonner";

const PRESTATION_OPTIONS = [
  "Bal Trad complet (2h-4h)",
  "Concert",
  "Initiation aux danses + Bal",
  "Cérémonie commémorative",
  "Autre prestation sur mesure",
];

const inputClass =
  "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3.5 text-sm text-stone-800 focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-2 block text-xs font-bold uppercase text-stone-700";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", prestation: PRESTATION_OPTIONS[0], event_info: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { settings } = useSite();

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, subject: form.prestation });
      setSent(true);
      setForm({ name: "", email: "", prestation: PRESTATION_OPTIONS[0], event_info: "", message: "" });
      toast.success("Votre message a bien été envoyé. Nous répondons sous 48h.");
    } catch (err) {
      toast.error(formatApiError(err, "L'envoi a échoué. Réessayez dans un instant."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Seo title="Contact et devis" description="Contactez Les Sonneurs de la Côte pour un bal, un concert, une cérémonie ou une animation : réponse rapide à votre demande de devis." path="/contact" />
      <section className="border-b border-stone-200 bg-[#1E1B18] py-16 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-200">{siteText(settings, "page_contact_label", "Demande de date")}</span>
          <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight sm:text-6xl">{siteText(settings, "page_contact_title", "Contact")}</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-300 sm:text-base">
            {siteText(settings, "page_contact_intro", "Une prestation à organiser, un projet à nous raconter ? Écrivez-nous, nous répondons sous 48h.")}
          </p>
        </div>
      </section>

      <section className="bg-[#FAF6F0] py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          {sent ? (
            <div data-testid="contact-success-message" className="rounded-2xl border border-stone-300 bg-white p-10 text-center shadow-md">
              <p className="font-display text-2xl font-bold uppercase text-[#1E1B18]">Merci !</p>
              <p className="mt-3 text-sm text-stone-600">
                Votre demande est bien arrivée. Nous vous répondrons sous 48h pour parler de votre événement.
              </p>
              <button
                data-testid="contact-send-another-button"
                onClick={() => setSent(false)}
                className="mt-6 text-xs font-bold uppercase text-[#B83227] hover:underline"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="space-y-6 rounded-2xl border border-stone-300 bg-white p-8 shadow-md sm:p-10"
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className={labelClass}>Nom ou Association *</label>
                  <input id="contact-name" data-testid="contact-name-input" type="text" className={inputClass} placeholder="Ex : Mairie, Festival..." value={form.name} onChange={update("name")} required />
                </div>
                <div>
                  <label htmlFor="contact-email" className={labelClass}>E-mail de contact *</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" className={inputClass} placeholder="contact@domaine.com" value={form.email} onChange={update("email")} required />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-prestation" className={labelClass}>Prestation recherchée</label>
                  <select id="contact-prestation" data-testid="contact-prestation-select" className={inputClass} value={form.prestation} onChange={update("prestation")}>
                    {PRESTATION_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-event-info" className={labelClass}>Lieu &amp; Date prévue</label>
                  <input id="contact-event-info" data-testid="contact-event-info-input" type="text" className={inputClass} placeholder="Ex : 21 juin, salle polyvalente" value={form.event_info} onChange={update("event_info")} />
                </div>
              </div>
              <div>
                <label htmlFor="contact-message" className={labelClass}>Message &amp; Besoins spécifiques *</label>
                <textarea id="contact-message" data-testid="contact-message-input" rows="5" className={inputClass} placeholder="Indiquez le contexte, les horaires prévus et les équipements du lieu..." value={form.message} onChange={update("message")} required />
              </div>
              <div className="flex flex-col items-center justify-between gap-4 border-t border-stone-200 pt-4 sm:flex-row">
                <span className="text-xs text-stone-500">Réponse sous 48h. Vos données restent confidentielles.</span>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="w-full rounded bg-[#B83227] px-8 py-3.5 text-xs font-bold uppercase tracking-wider text-white shadow transition-colors duration-300 hover:bg-[#96281E] disabled:opacity-60 sm:w-auto"
                >
                  {sending ? "Envoi en cours..." : "Envoyer ma demande de devis"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
